// src/services/academicService.js
import api from "../api/axios";

const BASE = "/academic";

const formDataConfig = { headers: { "Content-Type": "multipart/form-data" } };
const cfg = (data) => (data instanceof FormData ? formDataConfig : {});

/**
 * DRF بيرجّع أخطاء بأشكال مختلفة:
 *  { message } | { error } | { detail } | { field: ["msg"] }
 * الدالة دي بتطلع أول رسالة مفهومة.
 */
export const getApiError = (err, fallback = "حدث خطأ، حاول مرة أخرى") => {
  const data = err?.response?.data;
  if (!data || typeof data === "string") return fallback;
  if (data.message) return data.message;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  const first = Object.entries(data)[0];
  if (first) {
    const [field, msg] = first;
    return `${field}: ${Array.isArray(msg) ? msg[0] : msg}`;
  }
  return fallback;
};

// ============================================
// 1. CATEGORIES
// ============================================
export const academicCategoriesAPI = {
  // → { total_categories, categories: [...] }
  getAll: async () => (await api.get(`${BASE}/categories/`)).data,

  getById: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/`)).data,

  create: async (data) =>
    (await api.post(`${BASE}/categories/create/`, data, cfg(data))).data,

  update: async (categoryId, data) =>
    (await api.patch(`${BASE}/categories/${categoryId}/update/`, data, cfg(data)))
      .data,

  delete: async (categoryId) =>
    (await api.delete(`${BASE}/categories/${categoryId}/delete/`)).data,
};

// ============================================
// 2. EXAMS
// ============================================
export const academicExamsAPI = {
  // → { category: {id, name}, total_exams, exams: [...] }
  getByCategory: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/exams/`)).data,

  getById: async (examId) => (await api.get(`${BASE}/exams/${examId}/`)).data,

  create: async (categoryId, data) =>
    (
      await api.post(
        `${BASE}/categories/${categoryId}/exams/create/`,
        data,
        cfg(data)
      )
    ).data,

  update: async (examId, data) =>
    (await api.patch(`${BASE}/exams/${examId}/update/`, data, cfg(data))).data,

  delete: async (examId) =>
    (await api.delete(`${BASE}/exams/${examId}/delete/`)).data,
};

// ============================================
// 3. SKILLS
// ============================================
export const academicSkillsAPI = {
  // → { category: {id, name}, exam: {id, title}, total_skills, skills: [...] }
  getByExam: async (examId) =>
    (await api.get(`${BASE}/exams/${examId}/skills/`)).data,

  getById: async (skillId) => (await api.get(`${BASE}/skills/${skillId}/`)).data,

  create: async (examId, data) =>
    (await api.post(`${BASE}/exams/${examId}/skills/create/`, data, cfg(data)))
      .data,

  update: async (skillId, data) =>
    (await api.patch(`${BASE}/skills/${skillId}/update/`, data, cfg(data))).data,

  delete: async (skillId) =>
    (await api.delete(`${BASE}/skills/${skillId}/delete/`)).data,
};

// ============================================
// 4. QUESTIONS (student view + attempts)
// ============================================
export const academicQuestionsAPI = {
  getSkillQuestions: async (skillId, page = 1, pageSize = 20) =>
    (
      await api.get(`${BASE}/skills/${skillId}/questions/`, {
        params: { page, page_size: pageSize },
      })
    ).data,

  submitMCQ: async (skillId, questionType, questionId, data) =>
    (
      await api.post(
        `${BASE}/skills/${skillId}/questions/${questionType}/${questionId}/submit/`,
        data
      )
    ).data,

  showAnswer: async (skillId, questionType, questionId) =>
    (
      await api.post(
        `${BASE}/skills/${skillId}/questions/${questionType}/${questionId}/show-answer/`
      )
    ).data,

  getAttemptStatus: async (skillId, questionType, questionId) =>
    (
      await api.get(
        `${BASE}/skills/${skillId}/questions/${questionType}/${questionId}/attempt-status/`
      )
    ).data,

  submitWritingAnswer: async (questionId, data) =>
    (await api.post(`${BASE}/writing/questions/${questionId}/submit/`, data))
      .data,
};

// ============================================
// 5. CONTENT MANAGEMENT (admin) — 3 endpoints لكل الأنواع
// kind: vocabulary | grammar | writing
//       reading-passage | reading-question
//       listening-audio | listening-question
//       speaking-video  | speaking-question
//
// create:
//   - vocabulary / grammar / writing / *-passage / *-audio / *-video → academic_skill
//   - *-question → parent_id
// MCQ: options (list أو JSON string) + correct_answer (نص الإجابة الصحيحة)
// ============================================
export const academicContentAPI = {
  create: async (kind, data) =>
    (await api.post(`${BASE}/content/${kind}/create/`, data, cfg(data))).data,

  update: async (kind, pk, data) =>
    (await api.patch(`${BASE}/content/${kind}/${pk}/update/`, data, cfg(data)))
      .data,

  remove: async (kind, pk) =>
    (await api.delete(`${BASE}/content/${kind}/${pk}/delete/`)).data,
};

// ============================================
// 6. PROGRESS
// ============================================
export const academicProgressAPI = {
  getMyProgress: async () => (await api.get(`${BASE}/my-progress/`)).data,

  getByCategory: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/my-progress/`)).data,

  getByExam: async (examId) =>
    (await api.get(`${BASE}/exams/${examId}/my-progress/`)).data,

  getBySkill: async (skillId) =>
    (await api.get(`${BASE}/skills/${skillId}/my-progress/`)).data,
};

// ============================================
// 7. FAVORITES (على مستوى الاختبار)
// ============================================
export const academicFavoritesAPI = {
  toggle: async (examId) =>
    (await api.post(`${BASE}/exams/${examId}/favorite/`)).data,
  status: async (examId) =>
    (await api.get(`${BASE}/exams/${examId}/favorite/status/`)).data,
  list: async () => (await api.get(`${BASE}/my-favorites/`)).data,
};

// ============================================
// 8. SUBSCRIPTION (الاشتراك على مستوى الكاتيجوري)
// ============================================
export const academicSubscriptionAPI = {
  // student
  getStatus: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/subscription/status/`)).data,
  getPlans: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/subscription/plans/`)).data,
  pay: async (categoryId, data) =>
    (await api.post(`${BASE}/categories/${categoryId}/subscription/pay/`, data))
      .data,

  // admin — plans
  getAllPlansAdmin: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/subscription/plans/all/`))
      .data,
  createPlan: async (categoryId, data) =>
    (
      await api.post(
        `${BASE}/categories/${categoryId}/subscription/plans/create/`,
        data
      )
    ).data,
  updatePlan: async (planId, data) =>
    (await api.patch(`${BASE}/subscription/plans/${planId}/update/`, data)).data,
  deletePlan: async (planId) =>
    (await api.delete(`${BASE}/subscription/plans/${planId}/delete/`)).data,

  // admin — subscriptions
  adminList: async (params = {}) =>
    (await api.get(`${BASE}/subscription/admin/list/`, { params })).data,
  adminCreate: async (data) =>
    (await api.post(`${BASE}/subscription/admin/create/`, data)).data,
  adminUpdate: async (subscriptionId, data) =>
    (
      await api.patch(
        `${BASE}/subscription/admin/${subscriptionId}/update/`,
        data
      )
    ).data,
  adminDelete: async (subscriptionId) =>
    (await api.delete(`${BASE}/subscription/admin/${subscriptionId}/delete/`))
      .data,
};

// ============================================
// 9. AI GENERATION
// ============================================
export const academicAIAPI = {
  // Books
  extractBook: async (formData) =>
    (await api.post(`${BASE}/ai/extract-book/upload/`, formData, formDataConfig))
      .data,
  getExtractBookStatus: async (bookId) =>
    (await api.get(`${BASE}/ai/extract-book/${bookId}/status/`)).data,
  listExtractedBooks: async () =>
    (await api.get(`${BASE}/ai/extract-book/`)).data,

  // Media
  extractMedia: async (formData) =>
    (await api.post(`${BASE}/ai/extract-media/upload/`, formData, formDataConfig))
      .data,
  getExtractMediaStatus: async (mediaId) =>
    (await api.get(`${BASE}/ai/extract-media/${mediaId}/status/`)).data,
  listExtractedMedia: async () =>
    (await api.get(`${BASE}/ai/extract-media/`)).data,

  // Generation
  generateSkill: async (data) =>
    (await api.post(`${BASE}/ai/generate-skill/`, data)).data,
  addQuestionsToSkill: async (data) =>
    (await api.post(`${BASE}/ai/add-questions/`, data)).data,
  getJobStatus: async (jobId) =>
    (await api.get(`${BASE}/ai/jobs/${jobId}/status/`)).data,
  listJobs: async (params = {}) =>
    (await api.get(`${BASE}/ai/jobs/`, { params })).data,
};

export const academyContentAPI = {
  create: async (kind, data) =>
    (await api.post(`${BASE}/content/${kind}/create/`, data, cfg(data))).data,

  update: async (kind, pk, data) =>
    (await api.patch(`${BASE}/content/${kind}/${pk}/update/`, data, cfg(data))).data,

  remove: async (kind, pk) =>
    (await api.delete(`${BASE}/content/${kind}/${pk}/delete/`)).data,

  // تحويل نوع سؤال MCQ (مهارات MIXED فقط)
  convert: async (kind, pk, data) =>
    (await api.post(`${BASE}/content/${kind}/${pk}/convert/`, data, cfg(data))).data,
};